import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import api from '../../api/axios';
import UserNavbar from '../../components/UserNavbar';
import { toast } from 'react-toastify';
import './PaymentStyles.css';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Check if Stripe key is configured
if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set in environment variables');
}

function PaymentForm({ slotId, familyMemberId, patientNotes, medicalReportFiles, onPaymentSuccess, onPaymentError, setDoctor, setLoading }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card'); // Default to card
  const [slotInfo, setSlotInfo] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('new'); // 'new' | pm_xxx
  const [saveCard, setSaveCard] = useState(true);
  const [cardholderName, setCardholderName] = useState('');

  useEffect(() => {
    // Create payment intent when component mounts
    const createPaymentIntent = async () => {
      try {
        const response = await api.post('payments/create-intent/', {
          slot_id: slotId,
          family_member_id: familyMemberId || null,
        });

        setClientSecret(response.data.client_secret);
        setPaymentData({
          payment_intent_id: response.data.payment_intent_id,
          payment_id: response.data.payment_id,
          amount: response.data.amount,
          consultation_fee: response.data.consultation_fee,
          processing_fee: response.data.processing_fee,
          call_type: response.data.call_type || 'VIDEO',
        });

        const cards = Array.isArray(response.data.saved_cards) ? response.data.saved_cards : [];
        setSavedCards(cards);
        const defaultId = response.data.default_payment_method_id;
        if (defaultId && cards.some((c) => c.id === defaultId)) {
          setSelectedCardId(defaultId);
        } else if (cards.length > 0) {
          setSelectedCardId(cards[0].id);
        } else {
          setSelectedCardId('new');
        }

        // Set doctor info from response
        if (response.data.doctor_id) {
          setDoctor({
            id: response.data.doctor_id,
            name: response.data.doctor_name,
            specialization: response.data.doctor_specialization || '',
          });
        }

        // Fetch slot details for display
        try {
          const slotResponse = await api.get(`appointments/user/slot/${slotId}/`);
          if (slotResponse.data) {
            setSlotInfo(slotResponse.data);
          }
        } catch (slotErr) {
          console.error('Error fetching slot details:', slotErr);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        let errorMessage = 'Failed to initialize payment';
        if (err.response?.status === 403) {
          errorMessage = 'Access denied. Please make sure you are logged in and try again.';
        } else if (err.response?.status === 401) {
          errorMessage = 'Session expired. Please login again.';
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    if (slotId) {
      createPaymentIntent();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotId, familyMemberId]);

  const confirmAppointment = async (paymentIntentId) => {
    const formData = new FormData();
    formData.append('payment_intent_id', paymentIntentId);
    formData.append('slot_id', slotId);
    if (familyMemberId) formData.append('family_member_id', familyMemberId);
    if (patientNotes) formData.append('patient_notes', patientNotes);
    (medicalReportFiles || []).forEach((file) => {
      if (file) formData.append('medical_reports', file);
    });

    const confirmResponse = await api.post('payments/confirm/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    onPaymentSuccess({
      appointment_id: confirmResponse.data.appointment_id,
      payment_id: confirmResponse.data.payment_id,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !clientSecret) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      let stripeError;
      let paymentIntent;

      if (selectedCardId && selectedCardId !== 'new') {
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: selectedCardId,
        });
        stripeError = result.error;
        paymentIntent = result.paymentIntent;
      } else {
        if (!elements) {
          setProcessing(false);
          return;
        }
        const cardElement = elements.getElement(CardElement);
        const confirmOpts = {
          payment_method: {
            card: cardElement,
            billing_details: cardholderName.trim()
              ? { name: cardholderName.trim() }
              : undefined,
          },
        };
        if (saveCard) {
          confirmOpts.setup_future_usage = 'off_session';
        }
        const result = await stripe.confirmCardPayment(clientSecret, confirmOpts);
        stripeError = result.error;
        paymentIntent = result.paymentIntent;

        // Persist as default if newly saved
        if (!stripeError && paymentIntent?.status === 'succeeded' && saveCard && paymentIntent.payment_method) {
          try {
            const pmId =
              typeof paymentIntent.payment_method === 'string'
                ? paymentIntent.payment_method
                : paymentIntent.payment_method?.id;
            if (pmId) {
              await api.post('payments/cards/confirm-saved/', {
                payment_method_id: pmId,
                set_as_default: true,
              });
            }
          } catch (saveErr) {
            console.warn('Payment ok but could not mark card saved:', saveErr);
          }
        }
      }

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        onPaymentError(stripeError.message);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        try {
          await confirmAppointment(paymentData.payment_intent_id);
        } catch (confirmError) {
          console.error('Error confirming payment:', confirmError);
          const apiErr =
            confirmError.response?.data?.error ||
            confirmError.response?.data?.detail ||
            (typeof confirmError.response?.data === 'string'
              ? confirmError.response.data
              : null);
          const message = apiErr
            ? apiErr
            : confirmError.message?.includes('Network')
              ? 'Payment succeeded but network failed while creating appointment. Please check My Bookings — do not pay again.'
              : 'Payment succeeded but failed to create appointment. Check My Bookings before retrying.';
          setError(message);
          onPaymentError(message);
        }
      } else {
        setError('Payment was not successful');
        onPaymentError('Payment was not successful');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred during payment');
      onPaymentError(err.message || 'An error occurred during payment');
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  const brandLabel = (brand) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  if (!clientSecret || !paymentData) {
    if (error) {
      return (
        <div className="text-center py-5">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
          <button
            className="btn btn-primary mt-3"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      );
    }
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading payment form...</span>
        </div>
        <p className="mt-3 text-muted">Initializing payment...</p>
      </div>
    );
  }

  return (
    <div className="payment-form">
      {/* Payment Method Selection */}
      <div className="mb-4">
        <label className="form-label fw-bold mb-3">
          <i className="bi bi-credit-card me-2"></i>
          Select Payment Method
        </label>
        <div className="d-flex gap-3 mb-3">
          <button
            type="button"
            className={`btn ${paymentMethod === 'card' ? 'btn-primary' : 'btn-outline-primary'} flex-fill`}
            onClick={() => setPaymentMethod('card')}
            disabled={processing}
          >
            <i className="bi bi-credit-card-2-front me-2"></i>
            Credit/Debit Card
          </button>
          <button
            type="button"
            className={`btn ${paymentMethod === 'upi' ? 'btn-primary' : 'btn-outline-secondary'} flex-fill`}
            onClick={() => {
              toast.info('UPI payment will be available soon. Please use card payment for now.', {
                position: "bottom-right",
                autoClose: 3000,
              });
            }}
            disabled={true}
            title="Coming soon"
          >
            <i className="bi bi-phone me-2"></i>
            UPI (Coming Soon)
          </button>
        </div>
      </div>

      {/* Card Payment Form */}
      {paymentMethod === 'card' && (
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          {savedCards.length > 0 && (
            <div className="mb-3">
              <label className="form-label fw-bold">Saved cards</label>
              <div className="d-flex flex-column gap-2">
                {savedCards.map((card) => (
                  <label
                    key={card.id}
                    className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                    style={{
                      cursor: 'pointer',
                      borderColor: selectedCardId === card.id ? '#2563EB' : '#E5E7EB',
                      background: selectedCardId === card.id ? '#EFF6FF' : '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="savedCard"
                      className="form-check-input m-0"
                      checked={selectedCardId === card.id}
                      onChange={() => setSelectedCardId(card.id)}
                      disabled={processing}
                    />
                    <i className="bi bi-credit-card-2-front text-primary" />
                    <div className="flex-grow-1 min-w-0">
                      <p className="mb-0 fw-semibold small">
                        {brandLabel(card.brand)} •••• {card.last4}
                        {card.is_default && (
                          <span className="badge bg-primary ms-2" style={{ fontSize: 10 }}>Default</span>
                        )}
                      </p>
                      <small className="text-muted">
                        Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                      </small>
                    </div>
                  </label>
                ))}
                <label
                  className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                  style={{
                    cursor: 'pointer',
                    borderColor: selectedCardId === 'new' ? '#2563EB' : '#E5E7EB',
                    background: selectedCardId === 'new' ? '#EFF6FF' : '#fff',
                  }}
                >
                  <input
                    type="radio"
                    name="savedCard"
                    className="form-check-input m-0"
                    checked={selectedCardId === 'new'}
                    onChange={() => setSelectedCardId('new')}
                    disabled={processing}
                  />
                  <i className="bi bi-plus-circle text-primary" />
                  <div>
                    <p className="mb-0 fw-semibold small">Use a new card</p>
                    <small className="text-muted">Enter card details below</small>
                  </div>
                </label>
              </div>
            </div>
          )}

          {selectedCardId === 'new' && (
            <div className="mb-4">
              <label className="form-label fw-bold">
                <i className="bi bi-credit-card me-2"></i>
                Card Details
              </label>
              <div className="mb-2">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Cardholder name (optional)"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  disabled={processing}
                />
              </div>
              <div className="card-element-container" autoComplete="off">
                <CardElement options={cardElementOptions} />
              </div>
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="saveCardCheckout"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  disabled={processing}
                />
                <label className="form-check-label small" htmlFor="saveCardCheckout">
                  Save this card for future payments
                </label>
              </div>
              <small className="text-muted d-block mt-2">
                <i className="bi bi-info-circle me-1"></i>
                Test Card: 4242 4242 4242 4242 | Any future date | Any CVC
              </small>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Payment Error:</strong> {error}
            </div>
          )}

          {/* Payment Summary */}
          <div className="payment-summary mb-4">
            <h6 className="fw-bold mb-3">
              <i className="bi bi-receipt me-2"></i>
              Payment Summary
            </h6>
            {slotInfo && (
              <div className="mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted small">Appointment Date:</span>
                  <span className="fw-bold small">
                    {slotInfo.date ? new Date(slotInfo.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 'N/A'}
                  </span>
                </div>
                {slotInfo.start_time && (
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Time:</span>
                    <span className="fw-bold small">
                      {slotInfo.start_time.substring(0, 5)} - {slotInfo.end_time ? slotInfo.end_time.substring(0, 5) : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">
                <i className={`bi ${paymentData.call_type === 'AUDIO' ? 'bi-telephone' : 'bi-camera-video'} me-1`}></i>
                {paymentData.call_type === 'AUDIO' ? 'Audio' : 'Video'} Consultation Fee:
              </span>
              <span className="fw-bold">${paymentData.consultation_fee.toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">
                <i className="bi bi-gear me-1"></i>
                Processing Fee:
              </span>
              <span className="fw-bold">${paymentData.processing_fee.toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between pt-3 mt-3 border-top">
              <span className="fw-bold fs-5">
                <i className="bi bi-cash-stack me-2"></i>
                Total Amount:
              </span>
              <span className="fw-bold fs-5 text-primary">
                ${paymentData.amount.toFixed(2)} <small className="text-muted">USD</small>
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-primary btn-lg w-100 py-3"
            disabled={!stripe || processing}
          >
            {processing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing Payment...
              </>
            ) : (
              <>
                <i className="bi bi-lock-fill me-2"></i>
                Pay ${paymentData.amount.toFixed(2)} Now
              </>
            )}
          </button>

          <div className="text-center mt-3">
            <p className="text-muted small mb-2">
              <i className="bi bi-shield-check me-1 text-success"></i>
              Secure payment powered by Stripe
            </p>
            <p className="text-muted small mb-0">
              <i className="bi bi-lock me-1"></i>
              Your payment information is encrypted and secure
            </p>
          </div>
        </form>
      )}

      {/* UPI Payment Placeholder */}
      {paymentMethod === 'upi' && (
        <div className="alert alert-info" role="alert">
          <i className="bi bi-info-circle me-2"></i>
          UPI payment method will be available soon. Please use card payment for now.
        </div>
      )}
    </div>
  );
}

function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [slotId, setSlotId] = useState(null);
  const [familyMemberId, setFamilyMemberId] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  const patientNotes = location.state?.patientNotes || '';
  const medicalReportFiles = (() => {
    if (Array.isArray(location.state?.medicalReportFiles) && location.state.medicalReportFiles.length) {
      return location.state.medicalReportFiles;
    }
    if (location.state?.medicalReportFile) return [location.state.medicalReportFile];
    return [];
  })();

  /* ---------------- CSRF INITIALIZATION ---------------- */
  useEffect(() => {
    const initCsrf = async () => {
      try {
        // Initialize CSRF token by making a GET request
        await api.get('user/login/');
      } catch (err) {
        console.error("CSRF Init failed", err);
      }
    };
    initCsrf();
  }, []);

  useEffect(() => {
    const slotIdParam = searchParams.get('slot_id');
    const familyMemberIdParam = searchParams.get('family_member_id');

    console.log('PaymentPage - slot_id param:', slotIdParam);
    console.log('PaymentPage - family_member_id param:', familyMemberIdParam);

    if (!slotIdParam) {
      console.error('PaymentPage - No slot_id provided');
      setLoading(false);
      // Don't navigate immediately - let the component render the error message
      return;
    }

    const parsedSlotId = parseInt(slotIdParam);
    if (isNaN(parsedSlotId)) {
      console.error('PaymentPage - Invalid slot_id:', slotIdParam);
      setLoading(false);
      // Don't navigate immediately - let the component render the error message
      return;
    }

    console.log('PaymentPage - Setting slotId to:', parsedSlotId);
    setSlotId(parsedSlotId);

    if (familyMemberIdParam) {
      const parsedFamilyMemberId = parseInt(familyMemberIdParam);
      if (!isNaN(parsedFamilyMemberId)) {
        setFamilyMemberId(parsedFamilyMemberId);
      }
    }

    // Don't set loading to false here - let PaymentForm handle it
  }, [searchParams, navigate]);

  const handlePaymentSuccess = (data) => {
    navigate(`/user/payment-success?appointment_id=${data.appointment_id}&payment_id=${data.payment_id}`);
  };

  const handlePaymentError = (error) => {
    navigate(`/user/payment-failed?error=${encodeURIComponent(error)}`);
  };

  if (loading && !slotId) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!slotId) {
    // If we've finished loading but still no slotId, show error
    return (
      <div className="bg-light min-vh-100">
        <UserNavbar />
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Slot ID is required. Please go back and select a time slot.
              </div>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/user/dashboard')}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <UserNavbar />

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="d-flex align-items-center mb-4">
              <button
                className="btn btn-link text-decoration-none text-muted p-0 me-3"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left fs-4"></i>
              </button>
              <h2 className="fw-bold mb-0">Complete Payment</h2>
            </div>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              {/* Header */}
              <div className="card-header bg-primary text-white py-3">
                <h5 className="mb-0 fw-bold">
                  <i className="bi bi-credit-card me-2"></i>
                  Payment Information
                </h5>
              </div>

              <div className="card-body p-4 p-md-5">
                {/* Doctor & Appointment Info */}
                {doctor && (
                  <div className="mb-4 pb-4 border-bottom">
                    <h5 className="fw-bold mb-3">
                      <i className="bi bi-calendar-check me-2 text-primary"></i>
                      Appointment Details
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <p className="text-muted mb-1 small">Doctor Name</p>
                        <p className="fw-bold mb-0">
                          <i className="bi bi-person-badge me-2 text-primary"></i>
                          {doctor.name}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted mb-1 small">Specialization</p>
                        <p className="fw-bold mb-0">
                          <i className="bi bi-briefcase me-2 text-primary"></i>
                          {doctor.specialization || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {(patientNotes || medicalReportFiles.length > 0) && (
                      <div className="mt-3 p-3 bg-light rounded-3 border">
                        {patientNotes && (
                          <div className="mb-2">
                            <p className="text-muted mb-1 small fw-bold">
                              <i className="bi bi-chat-left-text text-primary me-1"></i> Patient Notes / Symptoms:
                            </p>
                            <p className="mb-0 small text-dark fst-italic">"{patientNotes}"</p>
                          </div>
                        )}
                        {medicalReportFiles.length > 0 && (
                          <div>
                            <p className="text-muted mb-1 small fw-bold">
                              <i className="bi bi-paperclip text-success me-1"></i> Attached Medical Reports:
                            </p>
                            <div className="d-flex flex-wrap gap-2">
                              {medicalReportFiles.map((file, index) => (
                                <span
                                  key={`${file.name}-${index}`}
                                  className="badge bg-success bg-opacity-10 text-success border border-success rounded-pill px-3 py-1 small"
                                >
                                  <i className="bi bi-file-earmark-medical me-1"></i> {file.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Form */}
                <Elements stripe={stripePromise}>
                  <PaymentForm
                    slotId={slotId}
                    familyMemberId={familyMemberId}
                    patientNotes={patientNotes}
                    medicalReportFiles={medicalReportFiles}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    setDoctor={setDoctor}
                    setLoading={setLoading}
                  />
                </Elements>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPage;
